import { ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  useApproveWorkAssignmentListAttachment,
  useDenyWorkAssignmentListAttachment,
  useUpdateWorkAssignmentListAttachment,
} from 'components/IAP/hooks/assignmentListHooks';
import { ICS204_OUTLINE_BTN } from './assignmentCardStyles';

type Ics204HeaderWorkflowMenuProps = {
  walaId: string | null;
  actualStatus: string | null;
  isCurrentUserReviewer: boolean;
  /** Hide while viewing a historical version. */
  disabled?: boolean;
};

/**
 * Submit / Review actions beside the Status cell so users don’t scroll the
 * expanded 204 body. Same mutations as the former footer buttons.
 */
const Ics204HeaderWorkflowMenu = ({
  walaId,
  actualStatus,
  isCurrentUserReviewer,
  disabled = false,
}: Ics204HeaderWorkflowMenuProps) => {
  const { mutate: update } = useUpdateWorkAssignmentListAttachment();
  const { mutate: approve } = useApproveWorkAssignmentListAttachment();
  const { mutate: deny } = useDenyWorkAssignmentListAttachment();

  if (disabled || !walaId) return null;

  const showSubmit = actualStatus === 'in_progress';
  const showReview =
    actualStatus === 'awaiting_review' && isCurrentUserReviewer;

  if (!showSubmit && !showReview) return null;

  const stopRowToggle = (e: { stopPropagation: () => void }) => {
    e.stopPropagation();
  };

  if (showSubmit) {
    return (
      <div
        className="tw:shrink-0"
        onClick={stopRowToggle}
        onMouseDown={stopRowToggle}
        onPointerDown={stopRowToggle}
        onKeyDown={stopRowToggle}
      >
        <DropdownMenu>
          <DropdownMenuTrigger
            render={
              <Button
                type="button"
                size="sm"
                variant="outline"
                className={ICS204_OUTLINE_BTN}
                data-testid="ics204-submit-menu"
                aria-label="Submit"
              >
                Submit
                <ChevronDown className="tw:h-3.5 tw:w-3.5" />
              </Button>
            }
          />
          <DropdownMenuContent
            align="end"
            sideOffset={4}
            className="dark"
            onClick={stopRowToggle}
          >
            <DropdownMenuItem
              data-testid="ics204-submit-completion"
              onClick={() => update({ walaId, proposedStatus: 'completed' })}
            >
              Submit Completion
            </DropdownMenuItem>
            <DropdownMenuItem
              data-testid="ics204-report-inability-to-complete"
              onClick={() => update({ walaId, proposedStatus: 'incomplete' })}
            >
              Report Inability to Complete
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    );
  }

  return (
    <div
      className="tw:shrink-0"
      onClick={stopRowToggle}
      onMouseDown={stopRowToggle}
      onPointerDown={stopRowToggle}
      onKeyDown={stopRowToggle}
    >
      <DropdownMenu>
        <DropdownMenuTrigger
          render={
            <Button
              type="button"
              size="sm"
              variant="outline"
              className={ICS204_OUTLINE_BTN}
              data-testid="ics204-review-menu"
              aria-label="Review"
            >
              Review
              <ChevronDown className="tw:h-3.5 tw:w-3.5" />
            </Button>
          }
        />
        <DropdownMenuContent
          align="end"
          sideOffset={4}
          className="dark"
          onClick={stopRowToggle}
        >
          <DropdownMenuItem
            data-testid="ics204-approve-complete"
            onClick={() => approve(walaId)}
          >
            Approve Complete
          </DropdownMenuItem>
          <DropdownMenuItem
            data-testid="ics204-report-incomplete"
            onClick={() => deny(walaId)}
          >
            Report Incomplete
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
};

export default Ics204HeaderWorkflowMenu;
